import { useCallback, MouseEvent, useContext, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutomationsDispatch } from 'pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import { format } from 'date-fns';
import { errorByStatus } from 'pages/AutomationEngine/ts-helpers';
import { ExecutionHistory, Step } from 'pages/AutomationEngine/types';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import {
  getHistoryStatusColor,
  getHistoryStatusIcon,
  getHistoryStatusLabel,
  getStatusDecoration,
} from './helpers';
import { StyledHistoryDetails, StyledHistoryDetailsLabel } from './styles';

export const HistoryDetails: FC<{
  step: Step;
  history: ExecutionHistory;
  historyRef?: (el: HTMLDivElement | null) => void;
}> = ({ step, history, historyRef }) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();

  const { selectedHistoryStep, setSelectedHistoryStep, runningDebugAction } =
    useContext(ExecutionHistoryContext);

  const handleShowError = useCallback(
    (event: MouseEvent<HTMLElement>, history: ExecutionHistory) => {
      event.stopPropagation();
      dispatch(actions.showHistoryErrorModal([history]));
    },
    [dispatch]
  );

  const handleFocusStep = useCallback(
    (history: ExecutionHistory) => {
      setSelectedHistoryStep(history);
      dispatch(actions.setFocusStep({ step: step.id }));
    },
    [dispatch, setSelectedHistoryStep, step]
  );
  const stepOutput = getStatusDecoration(history, step, t);
  const hasError = errorByStatus(history.status);

  const statusLabel = getHistoryStatusLabel(
    runningDebugAction === history.id ? 'running' : history.status,
    t
  );
  const statusColor = getHistoryStatusColor(
    runningDebugAction === history.id ? 'running' : history.status
  );
  const statusIcon = getHistoryStatusIcon(
    runningDebugAction === history.id ? 'running' : history.status
  );

  const clickable =
    Boolean(hasError || history.detailedLog) &&
    runningDebugAction !== history.id;

  return (
    <StyledHistoryDetails
      key={history.id}
      ref={historyRef}
      focusedStep={selectedHistoryStep?.id === history.id}
      onClick={() => handleFocusStep(history)}
    >
      <StyledHistoryDetailsLabel
        color={statusColor}
        clickable={clickable}
        onClick={
          clickable ? (event) => handleShowError(event, history) : undefined
        }
      >
        <Icon icon={statusIcon} color={statusColor} size="xs" />
        <Typography weight="semibold" color={statusColor} size="xs">
          {statusLabel}
          {stepOutput}
        </Typography>
      </StyledHistoryDetailsLabel>
      <Typography size="xs">
        {t('Order of execution: {{ order }}', { order: history.index + 1 })}
      </Typography>
      <span style={{ display: 'flex', gap: '4px' }}>
        <Typography size="xs">
          {format(history.updated || history.created, 'MM/dd/yyyy')}
        </Typography>
        <Typography weight="semibold" size="xs">
          {format(history.updated || history.created, 'HH:mm:ss')}
        </Typography>
      </span>
    </StyledHistoryDetails>
  );
};
